/**
 * One company in the brain's own list.
 *
 * The domain NAVIGATES here, unlike every other list that shows the brain. The
 * follow-up queue and scan-leads open the card over themselves on purpose -
 * their filters and scroll position are component state, and leaving throws
 * away a list somebody just built. This page is the opposite case: it is the
 * brain's own address, and the whole point of R7 is that one run has a link
 * that can be pointed at, from the bell and from a colleague's message both.
 *
 * The register link stays an EXTRA, drawn only when a register code exists. A
 * lead with no code is exactly the kind of site this tool was built for, and it
 * must never be the row that cannot be opened.
 */
import { Building2, CalendarClock, FileText, Loader2, Phone, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@wa/ui";
import type { BrainListRow } from "../../../lib/siteBrain";
import { VerdictDriftMark } from "../ariregister/brain";
import { StatusPill, fmtDate, fmtStamp } from "../../ui";
import type { T } from "../site-scanner/types";
import { brainCompanyPath } from "../prospecting/steps";
import { fitTone, isStale, starterLabel, statusKey, statusTone } from "./lib";

export function BrainRowItem({
  row,
  cols,
  now,
  me,
  t,
}: {
  row: BrainListRow;
  cols: string;
  now: Date;
  /** The reader's own user id, so their runs can say "You" rather than a name. */
  me: string;
  t: T;
}) {
  const stale = isStale(row, now);
  // A name is printed as it stands and never handed to `t()`: a colleague
  // called "Auto" would otherwise be translated into the autopilot.
  const starter = starterLabel(row, me);
  // ⚠ ONLY WHEN SOMETHING WAS REPAIRED, the same gate the follow-up queue uses.
  // A "0 fixed" mark is the negative half of the sentence, which is both an
  // accusation and the half the comparison is least able to prove. What got
  // worse rides in the tooltip, where it informs without shouting.
  const repaired = row.repaired && row.repaired.fixed > 0 ? row.repaired : null;
  const starterText = starter ? ("name" in starter ? starter.name : t(`brain.list.startedBy.${starter.key}`)) : null;
  return (
    <div className="crm-list-row relative" style={{ gridTemplateColumns: cols }}>
      <span className="min-w-0 pl-3">
        <span className="flex min-w-0 items-center gap-2">
          {/* The stretched overlay makes the whole row the target, the same way
              the follow-up queue and the campaign list do. */}
          <Link
            to={brainCompanyPath(row.domain)}
            className="crm-prow-n crm-trunc min-w-0 after:absolute after:inset-0 after:rounded-full"
            title={row.domain}
          >
            {row.domain}
          </Link>
          {/* Shares the squeeze rather than winning it: without `crm-trunc
              min-w-0` the pill keeps its full width and the domain absorbs
              every lost pixel, which is how a row ends up unable to say which
              company it is. */}
          {row.fit ? (
            <StatusPill tone={fitTone(row.fit)} className="crm-trunc min-w-0">
              {t(`brain.fit.decision.${row.fit}`)}
            </StatusPill>
          ) : null}
          {/* Beside the pill and not on the sub-line, because it is a statement
              ABOUT the pill. A caution two lines away from the thing it cautions
              against is read as a separate fact about the company. */}
          <VerdictDriftMark drift={row.repaired?.drift} t={t} />
        </span>
        {/* What the run produced and who asked for it, on one line. The starter
            joins the sub-line rather than taking a column of its own: this row
            already divides four tracks at a 220px floor, and a fifth would come
            out of the company name, which is the one thing the row must always
            be able to say. Dropped entirely when there is no answer - a dash
            reads as a person whose name was lost. */}
        <span className="crm-prow-s flex min-w-0 items-center gap-1.5">
          {/* The repair mark goes FIRST on this line. It is the only thing here
              that changes what an operator does next: a company that has been
              fixing things reads its post, and the argument count and the
              starter are context for a row already chosen. */}
          {repaired ? (
            <span
              className="shrink-0 font-semibold"
              style={{ color: "var(--color-success)" }}
              title={t("brainDue.repairedAt", { date: fmtDate(repaired.at), appeared: repaired.appeared })}
            >
              <Wrench className="mr-1 inline size-3" />
              {t("brainDue.repaired", { n: repaired.fixed })}
            </span>
          ) : null}
          <span className="crm-trunc" title={row.url ?? row.domain}>
            {row.arguments > 0 ? t("brain.list.argumentCount", { n: row.arguments }) : t("brain.list.noArguments")}
            {starterText ? ` \u00b7 ${starterText}` : ""}
          </span>
        </span>
      </span>

      <span className="flex min-w-0 items-center gap-2">
        <StatusPill tone={statusTone(row)} className="shrink-0">
          {row.status === "running" ? <Loader2 className="size-3 animate-spin" /> : null}
          {t(`brain.list.status.${statusKey(row)}`)}
        </StatusPill>
        {/* The tags themselves, not their count: "pictures, look" tells an
            operator which half of the run did not happen, and "2" tells them
            nothing they can act on. */}
        {row.degraded.length > 0 ? (
          <span className="crm-mut crm-trunc text-[12.5px]">
            {row.degraded.map((tag) => t(`brain.degradedStage.${tag}`)).join(", ")}
          </span>
        ) : null}
      </span>

      <span className="flex min-w-0 items-center gap-1.5">
        <span className="crm-tnum crm-trunc text-[12.5px]" title={fmtStamp(row.finishedAt ?? row.startedAt)}>
          {fmtStamp(row.finishedAt ?? row.startedAt)}
        </span>
        {/* Said as a word, because the date alone does not answer what the
            operator is actually asking: may I still quote this at somebody. */}
        {stale ? (
          <span
            className="shrink-0 text-[12px] font-bold text-[var(--color-warning)]"
            title={t("brain.list.staleHint")}
          >
            {t("brain.list.stale")}
          </span>
        ) : null}
      </span>

      <span className="flex items-center justify-end gap-1 pr-3">
        {/* Marks, not buttons. Each says a thing already exists for this run, so
            an operator can see at a glance which rows have been worked. */}
        {row.hasReport ? (
          <span className="crm-mut" title={t("brain.list.hasReport")} aria-label={t("brain.list.hasReport")}>
            <FileText className="size-3.5" />
          </span>
        ) : null}
        {row.calledAt ? (
          <span
            className="crm-mut"
            title={`${t("brain.list.called")}: ${fmtStamp(row.calledAt)}`}
            aria-label={t("brain.list.called")}
          >
            <Phone className="size-3.5" />
          </span>
        ) : null}
        {row.nextTouchAt ? (
          <span
            className={cn("shrink-0", "text-[var(--color-warning)]")}
            title={`${t("brain.list.hasPlan")}: ${fmtStamp(row.nextTouchAt)}`}
            aria-label={t("brain.list.hasPlan")}
          >
            <CalendarClock className="size-3.5" />
          </span>
        ) : null}
        {/* Above the row's stretched overlay, or the overlay eats the click. */}
        {row.regCode ? (
          <Link
            to={`/ariregister/${row.regCode}`}
            className="crm-cb crm-cb-sm relative z-[1]"
            title={t("brainDue.openCompany")}
            aria-label={t("brainDue.openCompany")}
          >
            <Building2 className="size-3.5" />
          </Link>
        ) : null}
      </span>
    </div>
  );
}
